import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Drawer, Button, SegmentedControl, NativeSelect, Text, ActionIcon, Group } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { useLoginContextSU } from "../../context/loginContextSU";
import { BE_API_SU_ONE, utils, role } from "../../utils/globalVariables";
import { safeValue } from "../../utils/globalFunctions";

import InputComponent from "../../components/general/InputComponent";
import InputWrapperCustom from "../../components/general/InputWrapperCustom";
import ModalResponse from "../../components/general/ModalResponse";

const SUDataUserEdit = ({
    stateShow,
    fnShow,
    stateData,
    fnSuccessEdit
}) => {

    const mountedRef = React.useRef(true);
    const { loginContextSU, handleSetLoginContextSU } = useLoginContextSU();

    const { super_user, ...newRole } = role;

    const subLevelSelector = (stateUserlevel) => {
        const inSubLevel = newRole[stateUserlevel].subLevel;

        const res = Object.keys(inSubLevel).map(subRoleName => ({
            value: inSubLevel[subRoleName].slugSubLevel,
            label: inSubLevel[subRoleName].caption,
        }));

        return (Array.isArray(res) && (res.length > 0)) ? res[0].value : '';
    }

    const notificationId = React.useRef({
        errorSubmitData: 'SUDataUserEdit-1',
        successSubmitData: 'SUDataUserEdit-2',
    })

    const constUserName = {
        id: 'edit--userName',
        label: 'Nama',
        withAsterisk: true,
        placeholder: 'Nama',
        value: '',
        description: '',
        error: '',
    }

    const constUserGender = {
        id: 'edit--userGender',
        label: 'Gender',
        withAsterisk: true,
        placeholder: 'Gender',
        value: '1',
        description: '',
        error: '',
    }

    const constUserLevel = {
        id: 'edit--userLevel',
        label: 'Jabatan',
        withAsterisk: true,
        placeholder: 'Jabatan',
        value: '',
        description: '',
        error: '',
    }

    const constUserSubLevel = {
        id: 'edit--userSubLevel',
        label: 'Sub Jabatan',
        withAsterisk: true,
        placeholder: 'Sub Jabatan',
        value: '',
        description: '',
        error: '',
    }

    const constUserEmail = {
        id: 'edit--userEmail',
        label: 'Email (Opsional)',
        withAsterisk: false,
        placeholder: 'Email',
        value: '',
        description: '',
        error: '',
    }

    const constUserIsActive = {
        id: 'edit--userIsActive',
        label: 'Status',
        withAsterisk: true,
        placeholder: 'Status',
        value: '',
        description: '',
        error: '',
    }

    const [data, setData] = React.useState({
        userName: constUserName,
        userGender: constUserGender,
        userLevel: constUserLevel,
        userSubLevel: constUserSubLevel,
        userEmail: constUserEmail,
        userIsActive: constUserIsActive,

        submitLoading: false,

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    React.useEffect(() => {
        setData(d => ({
            ...d,
            userName: { ...d.userName, value: stateData.user_name },
            userGender: { ...d.userGender, value: stateData.user_gender },
            userEmail: { ...d.userEmail, value: safeValue(stateData.user_email, '') },
            userLevel: { ...d.userLevel, value: stateData.user_level },
            userSubLevel: { ...d.userSubLevel, value: stateData.user_sub_level },
            userIsActive: { ...d.userIsActive, value: stateData.user_is_active },
        }))
    }, [stateData])

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const subLevelList = (stateLevelSlug) => {
        try {
            const subLevelThings = newRole[stateLevelSlug].subLevel;

            const res = Object.keys(subLevelThings).map(subRoleName => ({
                value: subLevelThings[subRoleName].slugSubLevel,
                label: subLevelThings[subRoleName].caption,
            }));

            return res;
        } catch (error) {
            return [];
        }
    }

    const handleSubmit = () => {
        setData(d => ({
            ...d,
            userName: { ...d.userName, error: '' },
            userGender: { ...d.userGender, error: '' },
            userEmail: { ...d.userEmail, error: '' },
            userLevel: { ...d.userLevel, error: '' },
            userSubLevel: { ...d.userSubLevel, error: '' },
            userIsActive: { ...d.userIsActive, error: '' },
        }));

        if (validator.isEmpty(data.userName.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userName: { ...d.userName, error: 'Nama User Harus Diisi !' } }));
        } else if (data.userEmail.value && !validator.isEmail(data.userEmail.value)) {
            setData(d => ({ ...d, userEmail: { ...d.userEmail, error: 'Format Email Tidak Valid' } }));
        } else if (validator.isEmpty(data.userLevel.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userLevel: { ...d.userLevel, error: 'Jabatan Harus Diisi !' } }));
        } else if (validator.isEmpty(data.userSubLevel.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userSubLevel: { ...d.userSubLevel, error: 'Sub Jabatan Harus Diisi !' } }));
        } else if (validator.isEmpty(data.userIsActive.value, { ignore_whitespace: true })) {
            setData(d => ({ ...d, userIsActive: { ...d.userIsActive, error: 'Status Harus Diisi !' } }));
        } else {
            setData(d => ({ ...d, submitLoading: true }));

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContextSU.syswebapp);
            formDT.append('id', stateData.user_id);
            formDT.append('nama', data.userName.value);
            formDT.append('gender', data.userGender.value);
            formDT.append('email', data.userEmail.value);
            formDT.append('level', data.userLevel.value);
            formDT.append('sub_level', data.userSubLevel.value);
            formDT.append('is_active', data.userIsActive.value);

            axios({
                method: 'post',
                url: BE_API_SU_ONE + '/edit_user',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                if (response.data.flag_status === 'isset') {

                    fnSuccessEdit();

                    notifications.show({
                        id: notificationId.current.successSubmitData,
                        title: utils.notification.success.caption,
                        message: 'Berhasil Submit Data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'duplicate-email') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: false,
                            content: `Email ${response.data.suspect} sudah pernah didaftarkan.`,
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContextSU(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContextSU(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                notifications.show({
                    id: notificationId.current.errorSubmitData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: false,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Drawer
                opened={stateShow}
                onClose={fnShow}
                withCloseButton={false}
                position="left"
                closeOnClickOutside={true}

                title={
                    <Group noWrap spacing='xs'>
                        <ActionIcon
                            size='lg'
                            variant="light"
                            radius='xl'
                            color="blue"
                            onClick={fnShow}
                        >
                            <IconArrowLeft size='1.25rem' stroke={1.5} />
                        </ActionIcon>

                        <Text>Edit User</Text>
                    </Group>
                }
                size='md'

                styles={() => ({
                    title: {
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }
                })}
            >
                <InputComponent
                    stateId={data.userName.id}
                    stateWithAsterisk={data.userName.withAsterisk}
                    stateLabel={data.userName.label}
                    stateDescription={data.userName.description}
                    statePlaceHolder={data.userName.placeholder}
                    stateValue={safeValue(data.userName.value, '')}
                    stateError={data.userName.error}

                    stateMT="1rem"

                    fnOnChange={(e) => setData(d => ({
                        ...d,
                        userName: {
                            ...d.userName,
                            value: e.target.value
                        }
                    }))}
                />

                <InputWrapperCustom
                    stateLabel={data.userGender.label}
                    stateWithAsterisk={data.userGender.withAsterisk}
                    stateError={data.userGender.error}
                    stateDescription={data.userGender.description}
                    stateMT="1.5rem"
                >
                    <SegmentedControl
                        fullWidth
                        value={safeValue(data.userGender.value, '1')}
                        onChange={(val) => setData(d => ({
                            ...d,
                            userGender: {
                                ...d.userGender,
                                value: val
                            }
                        }))}

                        data={[{ value: '1', label: 'Pria' }, { value: '0', label: 'Wanita' }]}
                    />
                </InputWrapperCustom>

                <InputComponent
                    stateId={data.userEmail.id}
                    stateWithAsterisk={data.userEmail.withAsterisk}
                    stateLabel={data.userEmail.label}
                    stateDescription={data.userEmail.description}
                    statePlaceHolder={data.userEmail.placeholder}
                    stateValue={safeValue(data.userEmail.value, '')}
                    stateError={data.userEmail.error}

                    stateMT="1rem"

                    fnOnChange={(e) => setData(d => ({
                        ...d,
                        userEmail: {
                            ...d.userEmail,
                            value: e.target.value
                        }
                    }))}
                />

                <InputWrapperCustom
                    stateLabel={data.userLevel.label}
                    stateWithAsterisk={data.userLevel.withAsterisk}
                    stateError={data.userLevel.error}
                    stateDescription={data.userLevel.description}
                    stateMT="1.5rem"
                >
                    <NativeSelect
                        value={safeValue(data.userLevel.value, '')}
                        onChange={(val) => {
                            setData(d => ({
                                ...d,
                                userLevel: { ...d.userLevel, value: val.target.value },
                                userSubLevel: { ...d.userSubLevel, value: subLevelSelector(val.target.value) }
                            }));
                        }}

                        data={Object.values(newRole).map(key => ({ value: key.slugLevel, label: key.caption }))}

                        styles={(tema) => ({
                            input: {
                                backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[6]
                            }
                        })}
                    />
                </InputWrapperCustom>

                <InputWrapperCustom
                    stateLabel={data.userSubLevel.label}
                    stateWithAsterisk={data.userSubLevel.withAsterisk}
                    stateError={data.userSubLevel.error}
                    stateDescription={data.userSubLevel.description}
                    stateMT="1.5rem"
                >
                    <NativeSelect
                        value={safeValue(data.userSubLevel.value, '')}
                        onChange={(val) => {
                            setData(d => ({ ...d, userSubLevel: { ...d.userSubLevel, value: val.target.value } }))
                        }}

                        data={subLevelList(data.userLevel.value)}

                        styles={(tema) => ({
                            input: {
                                backgroundColor: tema.colorScheme === 'light' ? '#fff' : tema.colors.dark[6]
                            }
                        })}
                    />
                </InputWrapperCustom>

                <InputWrapperCustom
                    stateLabel={data.userIsActive.label}
                    stateWithAsterisk={data.userIsActive.withAsterisk}
                    stateError={data.userIsActive.error}
                    stateDescription={data.userIsActive.description}
                    stateMT="1.5rem"
                >
                    <SegmentedControl
                        fullWidth
                        value={safeValue(data.userIsActive.value, '1')}
                        onChange={(val) => setData(d => ({
                            ...d,
                            userIsActive: {
                                ...d.userIsActive,
                                value: val
                            }
                        }))}

                        data={[{ value: '1', label: 'Aktif' }, { value: '0', label: 'Tidak Aktif' }]}
                    />
                </InputWrapperCustom>

                <Button
                    fullWidth
                    mt='1.5rem'
                    loading={data.submitLoading}
                    onClick={handleSubmit}
                >
                    Submit Data
                </Button>
            </Drawer>

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />
        </React.Fragment>
    )
}

export default SUDataUserEdit;